/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { t } from '@sportninja/common/i18n'
import parsers from '@sportninja/common/reducers/parsers'
import { connect } from 'react-redux'
import { bindActionToPromise } from '@sportninja/common/actions/utils'
import req from '@sportninja/common/api/request'
import { NewMapCard } from '../NewMapCard'
import ListStatusView from '../List/StatusView'
import useGetListItems from '../List/useGetListItems'
import { Flex } from '../Layout'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import typeActions from '@sportninja/common/actions/types'
import useWindowSize from 'src/hooks/useWindowSize'
import LocationModal from '../LocationModal'

const sorter = (a, b) => {
  const nameA = a.name_full,
    nameB = b.name_full
  if (nameA < nameB) {
    return -1
  } else if (nameA > nameB) {
    return 1
  }

  return 0
}

const Venues = ({
  entityType,
  id,
  slug,
  refreshKey,
  triggerRefresh,
  query,
  parentPermission,
  readCountries,
  organizationId,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 1024, [width])
  const { loading, error, items, sort, direction, getItems } = useGetListItems(
    slug,
    { query }
  )
  const [selectedVenue, setSelectedVenue] = useState(null)
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false)

  const entity = useMemo(
    () => (entityType === ENTITY_TYPES.org ? 'organizations' : 'schedules'),
    [entityType]
  )

  const updatePermission =
    parentPermission?.update || parentPermission?.admin ? true : false

  useEffect(() => {
    getItems(1, query)
  }, [refreshKey, sort, direction])

  const onDeleteVenue = useCallback(async (venueId) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        await req(`/${entity}/${id}/venues/${venueId}`, {
          method: 'DELETE',
        })
      } catch (error) {
        console.error(error)
        alert(
          `An error occurred when deleting the venue: ${
            error?.message || 'Unknown error'
          }`
        )
      } finally {
        triggerRefresh()
      }
    }
  }, [])

  useEffect(() => {
    readCountries()
  }, [])

  return (
    <>
      <Flex
        row
        flexWrap
        css={css`
          gap: 20px;
          padding-top: 16px;
          justify-content: flex-start;
          ${isMobile
            ? css`
                padding: 0 16px;
                padding-top: 16px;
              `
            : null}
        `}
      >
        {loading || error || items.length === 0 ? (
          <div style={{ margin: '0 auto' }}>
            <ListStatusView
              loading={loading}
              error={error}
              zeroItems={items.length === 0}
              noItemsText={t('common:location_plural', { count: 2 })}
            />
          </div>
        ) : (
          items.sort(sorter).map((item) => {
            const venue = parsers.venue(item)
            return (
              <NewMapCard
                name={venue?.fullName}
                description={venue?.addressString}
                latitude={venue?.coords?.[0] || null}
                longitude={venue?.coords?.[1] || null}
                key={venue.id}
                onDeleteClick={() => onDeleteVenue(venue.id)}
                onUpdateClick={() => {
                  setSelectedVenue(venue)
                  setIsLocationModalOpen(true)
                }}
                updatePermission={updatePermission}
                timeZone={venue?.timezone}
                isMobile={isMobile}
              />
            )
          })
        )}
      </Flex>
      <LocationModal
        isOpen={isLocationModalOpen}
        onClose={() => {
          setIsLocationModalOpen(false)
          setSelectedVenue(null)
        }}
        organizationId={organizationId}
        initialState={
          selectedVenue
            ? {
                id: selectedVenue?.id,
                name: selectedVenue?.fullName || '',
                address_line1: selectedVenue?.address?.street_1 || '',
                address_line2: selectedVenue?.address?.street_2 || '',
                city: selectedVenue?.address?.city || '',
                postcode: selectedVenue?.address?.postal_code || '',
                country_id: selectedVenue?.address?.country_id,
                province_id: selectedVenue?.address?.province_id,
                country: selectedVenue?.address?.country?.iso_3166_2 || '',
                province: selectedVenue?.address?.province?.iso_3166_2 || '',
              }
            : undefined
        }
        initialFacilities={selectedVenue?.facilities || []}
        onSuccess={() => {
          setIsLocationModalOpen(false)
          setSelectedVenue(null)
          triggerRefresh()
        }}
      />
    </>
  )
}

Venues.propTypes = {
  entityType: PropTypes.string,
  id: PropTypes.string.isRequired,
  onUpdate: PropTypes.func,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  venues: PropTypes.array,
}

const mapDispatchToProps = (dispatch) => {
  return {
    readCountries: bindActionToPromise(dispatch, typeActions.countries.request),
  }
}

export default connect(null, mapDispatchToProps)(Venues)
